import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';
import type { ClusterManager } from './ClusterManager';
import type { WorldConfig } from '../config/WorldConfig';
import type { FogRenderer } from '../objects/FogRenderer';
import type { FogState } from './FogState';
import { HEX_DIRECTIONS, axialKey } from '../math/HexMath';

const FLOATS_PER_FOG = 8;
const MAX_FOG_HEXES = 50_000;

/**
 * Coordinates fog-of-war state and rendering.
 * Listens to cluster-changed events, builds per-hex instance
 * data (position + 6-neighbor adjacency), and feeds FogRenderer.
 * Excludes sun hexes so suns glow through the fog.
 */
export class FogManager implements GameModule {
  readonly name = 'FogManager';
  readonly fogState: FogState;

  private _clusterMgr: ClusterManager;
  private _renderer: FogRenderer;
  private _config: WorldConfig;
  private _dirty = false;
  private _lastContentVersion = -1;

  private _sunKeyProvider: (() => ReadonlySet<number>) | null;
  private _contentVersionProvider: (() => number) | null;

  private _buffer = new Float32Array(MAX_FOG_HEXES * FLOATS_PER_FOG);
  private _hexQ = new Int32Array(MAX_FOG_HEXES);
  private _hexR = new Int32Array(MAX_FOG_HEXES);
  private _fogKeys = new Set<number>();

  constructor(
    clusterMgr: ClusterManager,
    renderer: FogRenderer,
    fogState: FogState,
    config: WorldConfig,
    sunKeyProvider: (() => ReadonlySet<number>) | null,
    contentVersionProvider: (() => number) | null,
  ) {
    this._clusterMgr = clusterMgr;
    this._renderer = renderer;
    this.fogState = fogState;
    this._config = config;
    this._sunKeyProvider = sunKeyProvider;
    this._contentVersionProvider = contentVersionProvider;
  }

  init(engine: Engine): void {
    engine.events.on('world:clusters-changed', () => { this._dirty = true; });
    engine.events.on('fog:reveals-changed', () => { this._dirty = true; });
    this._dirty = true;
    document.addEventListener('keydown', this._onKey, { capture: true });
  }

  private _onKey = (e: KeyboardEvent): void => {
    if (!e.ctrlKey || !e.shiftKey || e.key !== 'F') return;
    e.preventDefault();
    this._renderer.suppressed = !this._renderer.suppressed;
  };

  update(dt: number): void {
    this._renderer.advanceTime(dt);

    if (this._contentVersionProvider) {
      const ver = this._contentVersionProvider();
      if (ver !== this._lastContentVersion) {
        this._lastContentVersion = ver;
        this._dirty = true;
      }
    }

    if (!this._dirty) return;
    this._dirty = false;
    this._rebuild();
  }

  dispose(): void {
    document.removeEventListener('keydown', this._onKey, { capture: true });
    this._fogKeys.clear();
  }

  get fogHexCount(): number { return this._renderer.instanceCount; }

  private _rebuild(): void {
    this._fogKeys.clear();
    let fogCount = 0;

    const w = this._config.clusterWidth;
    const h = this._config.clusterHeight;
    const state = this.fogState;
    const sunKeys = this._sunKeyProvider?.() ?? null;

    this._clusterMgr.forEachCluster((cluster) => {
      for (let lr = 0; lr < h; lr++) {
        for (let lc = 0; lc < w; lc++) {
          if (fogCount >= MAX_FOG_HEXES) return;

          const gr = cluster.row * h + lr;
          const gc = cluster.col * w + lc;
          const off = (gr + (gr & 1)) >> 1;
          const q = gc - off;
          const r = gr;

          if (state.isRevealed(q, r)) continue;
          if (sunKeys?.has(axialKey(q, r))) continue;

          const idx = lr * w + lc;
          const base = fogCount * FLOATS_PER_FOG;
          this._buffer[base] = cluster.instanceData[idx * 5];
          this._buffer[base + 1] = cluster.instanceData[idx * 5 + 1];

          this._hexQ[fogCount] = q;
          this._hexR[fogCount] = r;
          this._fogKeys.add(axialKey(q, r));
          fogCount++;
        }
      }
    });

    for (let i = 0; i < fogCount; i++) {
      const base = i * FLOATS_PER_FOG + 2;
      const q = this._hexQ[i];
      const r = this._hexR[i];
      for (let d = 0; d < 6; d++) {
        const nk = axialKey(
          q + HEX_DIRECTIONS[d].q,
          r + HEX_DIRECTIONS[d].r,
        );
        this._buffer[base + d] = this._fogKeys.has(nk) ? 1.0 : 0.0;
      }
    }

    this._renderer.updateInstances(
      this._buffer.subarray(0, fogCount * FLOATS_PER_FOG),
      fogCount,
    );
  }
}
